import { preferGameState } from './game.js';

const DATABASE_NAME = 'dechess-games';
const DATABASE_VERSION = 1;
const STORE_NAME = 'games';
const CACHE_PREFIX = 'dechess:game-cache:v1:';

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function cacheKey(gameId) {
  return `${CACHE_PREFIX}${gameId}`;
}

function writeGameCache(game) {
  if (!game?.id || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(cacheKey(game.id), JSON.stringify(game));
  } catch {
    // Quota / private mode — IndexedDB remains the primary store.
  }
}

function readGameCache(gameId) {
  if (!gameId || typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(cacheKey(gameId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed?.id === gameId ? parsed : null;
  } catch {
    return null;
  }
}

function pickRicherGame(left, right) {
  if (!left) {
    return right || null;
  }
  if (!right) {
    return left;
  }
  // Concurrent ready-start forks share a revision with different startReady
  // bits. Prefer merge over updatedAt so lobby/board tabs cannot clobber each
  // other and leave both seats stuck on "waiting for opponent to confirm".
  if (
    left.status === 'starting'
    && right.status === 'starting'
    && (left.startReady?.white !== right.startReady?.white
      || left.startReady?.black !== right.startReady?.black)
  ) {
    return preferGameState(left, right);
  }
  const leftRevision = left.revision || 0;
  const rightRevision = right.revision || 0;
  if (leftRevision !== rightRevision) {
    return leftRevision > rightRevision ? left : right;
  }
  const leftMoves = left.moves?.length || 0;
  const rightMoves = right.moves?.length || 0;
  if (leftMoves !== rightMoves) {
    return leftMoves > rightMoves ? left : right;
  }
  const rank = { waiting: 0, starting: 1, playing: 2, finished: 3 };
  const leftRank = rank[left.status] ?? 0;
  const rightRank = rank[right.status] ?? 0;
  if (leftRank !== rightRank) {
    return leftRank > rightRank ? left : right;
  }
  return (left.updatedAt || 0) >= (right.updatedAt || 0) ? left : right;
}

export async function saveGame(game) {
  if (!game?.id) {
    throw new Error('Cannot persist a game without an ID');
  }
  // Write the sync cache first so a hard refresh mid-IDB transaction still recovers.
  writeGameCache(game);
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    await requestResult(transaction.objectStore(STORE_NAME).put(game));
  } finally {
    database.close();
  }
  return game;
}

export async function loadGame(gameId) {
  if (!gameId) {
    return null;
  }
  const cached = readGameCache(gameId);
  let stored = null;
  try {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      stored = (await requestResult(transaction.objectStore(STORE_NAME).get(gameId))) || null;
    } finally {
      database.close();
    }
  } catch {
    stored = null;
  }
  const picked = pickRicherGame(stored, cached);
  if (picked && (!stored || pickRicherGame(stored, picked) === picked)) {
    // Promote cache winner / merged ready-start back into IndexedDB.
    const shouldPromote = !stored
      || (stored.revision || 0) < (picked.revision || 0)
      || (stored.moves?.length || 0) < (picked.moves?.length || 0)
      || stored.status !== picked.status
      || stored.startReady?.white !== picked.startReady?.white
      || stored.startReady?.black !== picked.startReady?.black;
    if (shouldPromote) {
      try {
        await saveGame(picked);
      } catch {
        writeGameCache(picked);
      }
    }
  }
  return picked;
}

export async function listGames() {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const games = await requestResult(transaction.objectStore(STORE_NAME).getAll());
    return games.sort((left, right) => right.updatedAt - left.updatedAt);
  } finally {
    database.close();
  }
}
